import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Save, Upload, X, Plus, Trash2, Settings,
  Image, Palette, Users, FileText, DollarSign, ArrowLeft,
  Eye, ChevronDown, ChevronUp, GripVertical
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { TeamsTab, ParticipantTypesTab, EventDetailsTab, BrandingTab } from '../admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color presets for branding
const colorPresets = [
  { name: 'Green', primary: '#16a34a', secondary: '#059669' },
  { name: 'Blue', primary: '#2563eb', secondary: '#1d4ed8' },
  { name: 'Purple', primary: '#7c3aed', secondary: '#6d28d9' },
  { name: 'Orange', primary: '#ea580c', secondary: '#c2410c' },
  { name: 'Red', primary: '#dc2626', secondary: '#b91c1c' },
  { name: 'Teal', primary: '#0d9488', secondary: '#0f766e' },
];

export default function EventConfigManager({ eventId, onClose, demoMode = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [loadError, setLoadError] = useState(null);
  
  // Configuration state
  const [eventDetails, setEventDetails] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    registration_deadline: '',
    max_registrations: 100,
    image_url: ''
  });
  
  const [branding, setBranding] = useState({
    hero_image_url: '',
    logo_url: '',
    primary_color: '#16a34a',
    secondary_color: '#059669'
  });
  
  const [participantTypes, setParticipantTypes] = useState([
    { type: 'primary', label: 'Primary Member', fee: 0, description: 'Club member (free entry)', enabled: true },
    { type: 'adult', label: 'Adult Family', fee: 0, description: 'Spouse, parent, or adult family (free)', enabled: true },
    { type: 'junior', label: 'Junior (Under 18)', fee: 0, description: 'Children under 18 (free)', enabled: true },
    { type: 'guest_child', label: 'Guest Child', fee: 500, description: 'Non-member children', enabled: true },
    { type: 'guest_adult', label: 'Guest Adult', fee: 1000, description: 'Non-member adults', enabled: true }
  ]);
  
  const [teamOptions, setTeamOptions] = useState([
    { id: 'golfers', name: 'Golfers', color: '#16a34a', enabled: true },
    { id: 'gymkhana', name: 'Gymkhana', color: '#2563eb', enabled: true },
    { id: 'karenites', name: 'Karenites', color: '#dc2626', enabled: true },
    { id: 'juniors', name: 'Juniors', color: '#f59e0b', enabled: true }
  ]);
  
  const [formFields, setFormFields] = useState([]);
  const [activities, setActivities] = useState([]);
  
  // Fetch configuration
  // Default demo data for when API is unavailable
  const KCC_HERO_IMAGE = 'https://customer-assets-cm19k8pv.emergentagent.net/job_6bdd03ec-2e3d-4c40-849f-33d024d87b20/artifacts/ccmnkaam_IMG-20260806-WA0046%282%29.webp';
  
  const setDemoDefaults = useCallback(() => {
    setEventDetails({
      name: 'Karen Country Club Family Sports Day 2026',
      description: 'A fun-filled day of sports, games, family bonding, and friendly competition!',
      start_date: '2026-08-29',
      end_date: '2026-08-29',
      registration_deadline: '2026-08-25',
      max_registrations: 200,
      image_url: KCC_HERO_IMAGE
    });
    setBranding({
      hero_image_url: KCC_HERO_IMAGE,
      logo_url: '',
      primary_color: '#16a34a',
      secondary_color: '#059669'
    });
  }, []);
  
  const fetchConfig = useCallback(async () => {
    if (!eventId) return;
    
    setLoading(true);
    setLoadError(null);
    
    // If in demo mode or using demo ID, use demo defaults
    if (demoMode || eventId === 'demo') {
      setDemoDefaults();
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/events/${eventId}/config`);
      if (response.ok) {
        const data = await response.json();
        
        // Set event details
        if (data.event) {
          setEventDetails({
            name: data.event.name || '',
            description: data.event.description || '',
            start_date: data.event.start_date ? data.event.start_date.split('T')[0] : '',
            end_date: data.event.end_date ? data.event.end_date.split('T')[0] : '',
            registration_deadline: data.event.registration_deadline ? data.event.registration_deadline.split('T')[0] : '',
            max_registrations: data.event.max_registrations || 100,
            image_url: data.event.image_url || ''
          });
        }
        
        // Set branding
        if (data.branding) {
          setBranding(data.branding);
        }
        
        // Set participant types
        if (data.participant_types && data.participant_types.length > 0) {
          setParticipantTypes(data.participant_types);
        }
        
        // Set form fields
        if (data.form_fields && data.form_fields.length > 0) {
          setFormFields(data.form_fields);
        }
        
        // Set activities
        if (data.activities) {
          setActivities(data.activities);
        }
      } else {
        // API returned error - use demo defaults
        console.error('Config API error:', response.status);
        setLoadError(`Unable to load configuration (Status: ${response.status})`);
        setDemoDefaults();
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      setLoadError('Unable to connect to server');
      setDemoDefaults();
    } finally {
      setLoading(false);
    }
  }, [eventId, demoMode, setDemoDefaults]);
  
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);
  
  // Save configuration
  const handleSave = async () => {
    // Warn if in demo mode
    if (demoMode || eventId === 'demo') {
      toast.warning('Demo mode: Changes are not saved to the database');
      return;
    }
    
    setSaving(true);
    try {
      // Save event details and branding
      const configResponse = await fetch(`${API}/events/${eventId}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: eventDetails.name,
          description: eventDetails.description,
          start_date: eventDetails.start_date ? `${eventDetails.start_date}T00:00:00` : null,
          end_date: eventDetails.end_date ? `${eventDetails.end_date}T23:59:59` : null,
          registration_deadline: eventDetails.registration_deadline ? `${eventDetails.registration_deadline}T23:59:59` : null,
          max_registrations: eventDetails.max_registrations,
          hero_image_url: branding.hero_image_url || eventDetails.image_url,
          logo_url: branding.logo_url,
          primary_color: branding.primary_color,
          secondary_color: branding.secondary_color,
          participant_types: participantTypes,
          team_options: teamOptions.filter(t => t.enabled).map(t => t.name),
          form_fields: formFields
        })
      });
      
      if (!configResponse.ok) {
        const errorText = await configResponse.text();
        console.error('Save error response:', configResponse.status, errorText);
        
        if (configResponse.status === 500) {
          toast.error('Database connection error. Please try again later.');
        } else {
          toast.error(`Failed to save: ${configResponse.status}`);
        }
        return;
      }
      
      toast.success('Configuration saved successfully');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Unable to connect to server. Changes not saved.');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle image upload
  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API}/upload/file?folder=events`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.file_url.startsWith('/') 
          ? `${process.env.REACT_APP_BACKEND_URL}${data.file_url}`
          : data.file_url;
        
        if (field === 'hero_image_url') {
          setBranding({ ...branding, hero_image_url: imageUrl });
        } else if (field === 'logo_url') {
          setBranding({ ...branding, logo_url: imageUrl });
        }
        
        toast.success('Image uploaded successfully');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    }
  };
  
  // Update participant type
  const updateParticipantType = (index, field, value) => {
    const updated = [...participantTypes];
    updated[index] = { ...updated[index], [field]: value };
    setParticipantTypes(updated);
  };
  
  // Toggle form field
  const toggleFormField = (index) => {
    const updated = [...formFields];
    updated[index] = { ...updated[index], enabled: !updated[index].enabled };
    setFormFields(updated);
  };
  
  // Tabs configuration
  const tabs = [
    { id: 'details', label: 'Event Details', icon: Calendar },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'pricing', label: 'Participant Types', icon: DollarSign },
    { id: 'teams', label: 'Teams', icon: Users },
    { id: 'fields', label: 'Form Fields', icon: FileText },
    { id: 'activities', label: 'Activities', icon: Settings }
  ];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-2xl shadow-xl max-w-4xl mx-auto" data-testid="event-config-manager">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b">
        <div className="flex items-center gap-4">
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-900">Event Configuration</h2>
            <p className="text-sm text-gray-500">{eventDetails.name || 'Configure your event'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => window.open(`/family-sports-day`, '_blank')}
            className="gap-2"
          >
            <Eye className="w-4 h-4" />
            Preview
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 gap-2"
            data-testid="save-config-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
      
      {/* Tab Content */}
      <div className="p-6">
        {/* Event Details Tab */}
        {activeTab === 'details' && (
          <EventDetailsTab 
            eventDetails={eventDetails} 
            setEventDetails={setEventDetails} 
          />
        )}
        
        {/* Branding Tab */}
        {activeTab === 'branding' && (
          <div className="space-y-6">
            {/* Hero Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hero Image</label>
              <div className="flex items-start gap-4">
                {branding.hero_image_url ? (
                  <div className="relative w-48 h-32 rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={branding.hero_image_url} 
                      alt="Hero" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => setBranding({ ...branding, hero_image_url: '' })}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-48 h-32 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                    <Image className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => handleImageUpload(e, 'hero_image_url')}
                      className="hidden"
                      data-testid="hero-image-upload"
                    />
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors">
                      <Upload className="w-4 h-4" />
                      Upload Image
                    </div>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">Recommended: 1200x600px, max 10MB</p>
                  
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">Or enter image URL:</label>
                    <input
                      type="url"
                      value={branding.hero_image_url}
                      onChange={e => setBranding({ ...branding, hero_image_url: e.target.value })}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Color Scheme */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Color Scheme</label>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
                {colorPresets.map(preset => (
                  <button
                    key={preset.name}
                    onClick={() => setBranding({ ...branding, primary_color: preset.primary, secondary_color: preset.secondary })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      branding.primary_color === preset.primary 
                        ? 'border-gray-900 shadow-md' 
                        : 'border-transparent hover:border-gray-200'
                    }`}
                  >
                    <div className="flex gap-1 mb-2">
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.primary }} />
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.secondary }} />
                    </div>
                    <p className="text-xs font-medium">{preset.name}</p>
                  </button>
                ))}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={branding.primary_color}
                      onChange={e => setBranding({ ...branding, primary_color: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.primary_color}
                      onChange={e => setBranding({ ...branding, primary_color: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm border rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Secondary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={branding.secondary_color}
                      onChange={e => setBranding({ ...branding, secondary_color: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.secondary_color}
                      onChange={e => setBranding({ ...branding, secondary_color: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm border rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Participant Types Tab */}
        {activeTab === 'pricing' && (
          <ParticipantTypesTab 
            participantTypes={participantTypes} 
            setParticipantTypes={setParticipantTypes} 
          />
        )}
        
        {/* Teams Tab */}
        {activeTab === 'teams' && (
          <TeamsTab 
            teamOptions={teamOptions} 
            setTeamOptions={setTeamOptions} 
          />
        )}
        
        {/* Form Fields Tab */}
        {activeTab === 'fields' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Enable or disable form fields for participant registration.
            </p>
            
            {formFields.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                No form fields configured. Default fields will be used.
              </p>
            ) : (
              <div className="space-y-2">
                {formFields.map((field, index) => (
                  <div 
                    key={field.field}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      field.enabled ? 'bg-white' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={field.enabled}
                          onChange={() => toggleFormField(index)}
                          className="w-4 h-4 text-green-600 rounded"
                        />
                      </label>
                      <div>
                        <p className={`font-medium ${field.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                          {field.label}
                        </p>
                        <p className="text-xs text-gray-500">
                          {field.type} • {field.required ? 'Required' : 'Optional'} • 
                          For: {field.for_types.join(', ')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Activities Tab */}
        {activeTab === 'activities' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600">
                {activities.length} activities configured
              </p>
              <Button variant="outline" size="sm" className="gap-2">
                <Plus className="w-4 h-4" />
                Add Activity
              </Button>
            </div>
            
            {activities.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                No activities configured yet.
              </p>
            ) : (
              <div className="space-y-3">
                {activities.map(activity => (
                  <div 
                    key={activity.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-white hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${activity.color}20` }}
                      >
                        <span style={{ color: activity.color }}>●</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{activity.name}</p>
                        <p className="text-sm text-gray-500">
                          {activity.sport} • {activity.activity_type} • {activity.scheduled_time}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-green-600">
                        KES {activity.fee_per_team || activity.fee_per_participant || 0}
                      </span>
                      <span className="text-xs text-gray-500">
                        {activity.current_teams || 0}/{activity.max_teams || '∞'} teams
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
